import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { DataGrid } from "@mui/x-data-grid";
// import { Button } from '@mui/material';
import { FaEdit, FaTrash } from "react-icons/fa";
import { v4 as uuidv4 } from "uuid";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { AWS_URL } from "../../Constant";

const getServerProductId = (item) =>
  item?._id || item?.productId || item?.product_id || item?.id || "";

const StoreSales = ({ storeSalesData, store_id }) => {
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editedProduct, setEditedProduct] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState("success");
  const [searchQuery, setSearchQuery] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [filterProductList, setFilterProductList] = useState([]);
  const [salesStocks, setSalesStocks] = useState([]);
  const resolveItemIdentifier = (row) =>
    row?.serverProductId || getServerProductId(row) || row?.barcode || row?.model_number || "";

  const isSameSalesItem = (item, row, resolvedId) => {
    if (item?._id && resolvedId && String(item._id) === String(resolvedId)) {
      return true;
    }
    const sameBarcode =
      String(item?.barcode || "") !== "" &&
      String(item?.barcode || "") === String(row?.barcode || "");
    const sameModel =
      String(item?.model_number || "") !== "" &&
      String(item?.model_number || "") === String(row?.model_number || "");

    if (sameBarcode && sameModel) {
      return true;
    }

    return false;
  };

const flattenData = (products) => {
    return products.reduce((acc, item) => {
      (item.datas || []).forEach((stock, index) => {
        const serverProductId = getServerProductId(stock);
        acc.push({
          customerName: item.customerName,
          customerAddress: item.customerAddress,
          customerNumber: item.customerNumber,
          EmpId: item.EmpId,
          date: item.date,
          paymentMode: item.paymentMode,
          transactionId: item.transactionId,
          BillNo: item.BillNo,
          totalAmtWithoutGst: item.totalAmtWithoutGst,
          gst: item.gst,
          subtotal: item.subtotal,
          barcode: stock.barcode,
          brand: stock.brand,
          model_number: stock.model_number,
          productName: stock.productName,
          serialNumber: stock.serialNumber,
          quantity: stock.quantity,
          mrp1: stock.mrp1,
          landingCost: stock.landingCost,
          discount: stock.discount,
          cutoffprice: stock.cutoffprice,
          rowId: serverProductId || `${item?._id || item?.BillNo || "sale"}-${index}-${uuidv4()}`,
          serverProductId,
          saleId: item._id || "",
        });
      });
      return acc;
    }, []);
  };

  
  useEffect(() => {
    const salesReport = storeSalesData["Sales"] || [];
    const totalSalesStocks = salesReport.map((sales, idx) => ({
      ...sales,
      _id: sales._id || uuidv4(),
    }));
    setSalesStocks(totalSalesStocks);
    setFilterProductList(totalSalesStocks);
  }, [storeSalesData]);

  const handleDelete = async (row) => {
    const productId = resolveItemIdentifier(row);
    if (!productId) {
      setStatusType("error");
      setStatusMessage("Unable to delete: missing item identifier for this sales row.");
      return;
    }

    const shouldDelete = window.confirm("Are you sure you want to delete this sales item?");
    if (!shouldDelete) {
      return;
    }

    try {
      const jwtToken = Cookies.get("jwt_token");
      const encodedStoreId = encodeURIComponent(String(store_id || ""));
      const encodedProductId = encodeURIComponent(String(productId));
      const url = `${AWS_URL}/deleteProduct/${encodedStoreId}/${encodedProductId}`;
      const option = {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
      };

      const response = await fetch(url, option);
      let data = {};
      try {
        data = await response.json();
      } catch (error) {
        data = { error: "Invalid server response" };
      }

      if (!response.ok || data?.success === false) {
        setStatusType("error");
        setStatusMessage(data?.error || data?.message || `Delete failed with status ${response.status}`);
        return;
      }

      setSalesStocks((prev) =>
        prev
          .map((sale) => {
            const isTargetSale =
              (row?.saleId && String(sale?._id || "") === String(row.saleId)) ||
              (row?.BillNo && String(sale?.BillNo || "") === String(row.BillNo));

            if (!isTargetSale) {
              return sale;
            }

            return {
              ...sale,
              datas: (sale.datas || []).filter((item) => !isSameSalesItem(item, row, productId)),
            };
          })
          .filter((sale) => (sale.datas || []).length > 0)
      );
      setStatusType("success");
      setStatusMessage("Sales item deleted successfully.");
    } catch (error) {
      setStatusType("error");
      setStatusMessage(error?.message || "Delete failed. Please try again.");
    }
  };

  const columns = [
    {
      field: "EmpId",
      headerName: "EmpId",
      width: 150,
      headerClassName: "custom-header",
      headerAlign: "center",
    },
    {
      field: "customerName",
      headerName: "customerNames",
      width: 170,
      headerClassName: "custom-header",
      headerAlign: "center",
    },
    {
      field: "customerNumber",
      headerName: "customerNumber",
      width: 160,
      headerClassName: "custom-header",
      headerAlign: "center",
    },
    {
      field: "date",
      headerName: "Date",
      width: 130,
      headerClassName: "custom-header",
      headerAlign: "center",
      valueGetter: (params) => {
        const date = params.row.date;
        if (date) {
          const dateObj = new Date(date);
          return dateObj.toISOString().slice(0, 10); // Format to yy-mm-dd
        }
        return ""; // Return empty if no date
      },
    },
    {
      field: "productName",
      headerName: "productName",
      type: "number",
      width: 220,
      headerClassName: "custom-header",
      headerAlign: "center",
    },
    {
      field: "brand",
      headerName: "brand",
      width: 120,
      headerClassName: "custom-header",
      headerAlign: "center",
    },
    {
      field: "barcode",
      headerName: "barcode",
      width: 110,
      headerClassName: "custom-header",
      headerAlign: "center",
    },
    {
      field: "model_number",
      headerName: "Model_number",
      width: 170,
      headerClassName: "custom-header",
      headerAlign: "center",
    },

    {
      field: "quantity",
      headerName: "quantity",
      type: "number",
      width: 100,
      headerClassName: "custom-header",
      headerAlign: "center",
    },
    {
      field: "gst",
      headerName: "gst",
      type: "number",
      width: 100,
      headerClassName: "custom-header",
      headerAlign: "center",
    },
    {
      field: "subtotal",
      headerName: "subtotal",
      type: "number",
      width: 150,
      headerClassName: "custom-header",
      headerAlign: "center",
    },
    {
      field: "edit",
      headerName: "Edit",
      headerClassName: "custom-header",
      sortable: false,
      width: 80,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleEdit(params.row)}
        >
          <FaEdit />
        </Button>
      ),
    },
    {
      field: "delete",
      headerName: "Delete",
      headerClassName: "custom-header",

      sortable: false,
      width: 100,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="secondary"
          onClick={() => handleDelete(params.row)}
        >
          <FaTrash />
        </Button>
      ),
    },
  ];

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setEditedProduct({ ...product });
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedProduct(null);
    setEditedProduct(null);
    setSaveLoading(false);
  };

  const handleSaveEdit = async () => {
    const serverProductId = resolveItemIdentifier(selectedProduct);

    if (!serverProductId) {
      setStatusType("error");
      setStatusMessage("Unable to edit: missing item identifier for this sales row.");
      return;
    }

    try {
      setSaveLoading(true);
      const jwtToken = Cookies.get("jwt_token");
      const encodedStoreId = encodeURIComponent(String(store_id || ""));
      const encodedProductId = encodeURIComponent(String(serverProductId));
      const url = `${AWS_URL}/editProduct/${encodedStoreId}/${encodedProductId}`;
      const payload = {
        ...editedProduct,
        productId: serverProductId,
        saleId: selectedProduct?.saleId || "",
        billNo: selectedProduct?.BillNo || "",
        barcode: editedProduct?.barcode || selectedProduct?.barcode || "",
        model_number: editedProduct?.model_number || selectedProduct?.model_number || "",
      };

      const option = {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
        body: JSON.stringify(payload),
      };
      const response = await fetch(url, option);
      if (response.ok) {
        setSalesStocks((prev) =>
          prev.map((sale) => {
            const isTargetSale =
              (selectedProduct?.saleId &&
                String(sale?._id || "") === String(selectedProduct.saleId)) ||
              (selectedProduct?.BillNo &&
                String(sale?.BillNo || "") === String(selectedProduct.BillNo));

            if (!isTargetSale) {
              return sale;
            }

            return {
              ...sale,
              datas: (sale.datas || []).map((item) =>
                isSameSalesItem(item, selectedProduct, serverProductId)
                  ? {
                      ...item,
                      ...editedProduct,
                    }
                  : item
              ),
            };
          })
        );
        setStatusType("success");
        setStatusMessage("Sales item updated successfully.");
        handleCloseEditDialog();
      } else {
        const data = await response.json().catch(() => ({}));
        setStatusType("error");
        setStatusMessage(data?.message || "Update failed on server.");
      }
    } catch (error) {
      setStatusType("error");
      setStatusMessage(error?.message || "Update failed. Please try again.");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setEditedProduct((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleFilterData = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setFromDate("");
    setToDate("");
  };
  useEffect(() => {
    const allSalesStocks = flattenData(salesStocks);
const regex = new RegExp(searchQuery, "i");
    const isWithinDateRange = (value) => {
      if (!fromDate && !toDate) {
        return true;
      }

      const rowDate = value ? new Date(value) : null;
      if (!rowDate || Number.isNaN(rowDate.getTime())) {
        return false;
      }

      if (fromDate) {
        const from = new Date(fromDate);
        from.setHours(0, 0, 0, 0);
        if (rowDate < from) {
          return false;
        }
      }

      if (toDate) {
        const to = new Date(toDate);
        to.setHours(23, 59, 59, 999);
        if (rowDate > to) {
          return false;
        }
      }

      return true;
    };

    const filteredData = allSalesStocks.filter((product) => {
      const matchesSearch =
        regex.test(product.productName) ||
        regex.test(product.brand) ||
        regex.test(product.RetailStoreCode) ||
        regex.test(product.model_number) ||
        regex.test(product.EmpId);

      return matchesSearch && isWithinDateRange(product.date);
    });

    setFilterProductList(filteredData);
  }, [salesStocks, searchQuery, fromDate, toDate]);

  const getExportRows = () => {
    return filterProductList.map((item) => ({
      EmpId: item.EmpId,
      customerNames: item.customerName,
      customerNumber: item.customerNumber,
      Date: item.date ? new Date(item.date).toISOString().slice(0, 10) : "",
      productName: item.productName,
      brand: item.brand,
      barcode: item.barcode,
      Model_number: item.model_number,
      quantity: item.quantity,
      gst: item.gst,
      subtotal: item.subtotal,
    }));
  };

  const downloadExcel = () => {
    const rows = getExportRows();
    if (!rows.length) {
      return;
    }
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "StoreSales");
    XLSX.writeFile(workbook, "store-sales-report.xlsx");
  };

  const downloadPdf = () => {
    const rows = getExportRows();
    if (!rows.length) {
      return;
    }

    const doc = new jsPDF("l", "mm", "a4");
    autoTable(doc, {
      head: [Object.keys(rows[0] || {})],
      body: rows.map((row) => Object.values(row)),
      styles: { fontSize: 8 },
      margin: { top: 15 },
    });
    doc.save("store-sales-report.pdf");
  };

  return (
    <>
      <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50 p-3 shadow-sm">
        <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <h1 className="text-lg font-semibold text-slate-800">State: {storeSalesData.state}</h1>
          <h2 className="text-xl font-bold text-rose-600">Sales Reports</h2>
        </div>

        {statusMessage && (
          <div
            className={`mb-3 rounded-md px-3 py-2 text-sm font-medium ${
              statusType === "error"
                ? "bg-red-100 text-red-700"
                : "bg-green-100 text-green-700"
            }`}
          >
            {statusMessage}
          </div>
        )}

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col">
            <label htmlFor="admin-store-sales-search" className="mb-1 text-xs font-semibold text-slate-600">
              Search
            </label>
            <input
              type="search"
              id="admin-store-sales-search"
              className="h-9 w-64 rounded-md border border-slate-300 bg-white px-2 text-sm placeholder:text-gray-500"
              value={searchQuery}
              onChange={handleFilterData}
              placeholder="Search sales data"
            />
          </div>

          <div className="flex flex-col">
            <label className="mb-1 text-xs font-semibold text-slate-600">From</label>
            <input
              type="date"
              className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
            />
          </div>

          <div className="flex flex-col">
            <label className="mb-1 text-xs font-semibold text-slate-600">To</label>
            <input
              type="date"
              className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
            />
          </div>

          <div className="ml-auto flex gap-2">
            <button
              className="h-9 rounded-md bg-slate-600 px-3 text-sm font-semibold text-white"
              onClick={handleClearFilters}
            >
              Clear Filters
            </button>
            <button
              className="h-9 rounded-md bg-green-600 px-3 text-sm font-semibold text-white"
              onClick={downloadExcel}
            >
              Download Excel
            </button>
            <button
              className="h-9 rounded-md bg-red-600 px-3 text-sm font-semibold text-white"
              onClick={downloadPdf}
            >
              Download PDF
            </button>
          </div>
        </div>
      </div>

      <div style={{ height: 450, width: "100%", overflow: "auto" }}>
        <DataGrid
          sx={{
            boxShadow: 2,
            border: 2,
            borderColor: "#9F2725",
            "& .custom-header": {
              backgroundColor: "#9F2725",
              color: "white",
            },
            "& .MuiDataGrid-colCell,  .MuiDataGrid-cell:hover": {
              color: "primary.main",
            },
            "& .MuiDataGrid-sortIcon ": {
              backgroundColor: "red",
            },
            ".MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            },
          }}
          // rows={storeSalesData.Sales}
          rows={filterProductList}
          columns={columns}
          getRowId={(row) => row.rowId}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 100 },
            },
          }}
          pageSizeOptions={[5, 15]}
          checkboxSelection
          disableRowSelectionOnClick
        />
      </div>

      <Dialog open={openEditDialog} onClose={handleCloseEditDialog}>
        <DialogTitle>Edit Product</DialogTitle>
        <DialogContent>
          {editedProduct && (
            <>
              <TextField
                label="Product Name"
                value={editedProduct.productName}
                onChange={(e) => handleChange("productName", e.target.value)}
                fullWidth
                margin="dense"
              />
              {/* Add text fields for other fields as needed */}
              <TextField
                label="Brand"
                value={editedProduct.brand}
                onChange={(e) => handleChange("brand", e.target.value)}
                fullWidth
                margin="dense"
              />
              <TextField
                label="Model_number"
                value={editedProduct.model_number}
                onChange={(e) => handleChange("model_number", e.target.value)}
                fullWidth
                margin="dense"
              />
              <TextField
                label="Barcode"
                value={editedProduct.barcode}
                onChange={(e) => handleChange("barcode", e.target.value)}
                fullWidth
                margin="dense"
              />
              <TextField
                label="Quantity"
                value={editedProduct.quantity}
                onChange={(e) => handleChange("quantity", e.target.value)}
                fullWidth
                margin="dense"
                type="number"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSaveEdit} color="primary" disabled={saveLoading}>
            {saveLoading ? "Saving..." : "Save"}
          </Button>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StoreSales;
